import { ExpertState, ExpertStateNew, SessionStatus } from "@/autoGeneratedGlobalTypes";
import { MIN_SESSION_DURATION } from "@/constants";
import { EdgesType } from "@/globalTypes";

import { isBrowser } from "./env";

export const getActualMinSessionDuration = (minSessionDuration: number) =>
  (minSessionDuration > MIN_SESSION_DURATION ? minSessionDuration : MIN_SESSION_DURATION);

export const isNullOrUndefined = <T>(v: T | null | undefined): v is T =>
  typeof v === "undefined" || v === null;

export const isNotNullOrUndefined = <T>(v: T | null | undefined): v is T =>
  !isNullOrUndefined(v);

/** Legacy порог БМ (M7) для пользователей без активного welcome offer. */
export const getLegacyFreeMinutesThreshold = (): number =>
  parseInt(process.env.GATSBY_FREE_MINUTES_COUNT ?? "7", 10);

export type FreeMinutesEligibilityContext = {
  isWbOfferActive?: boolean;
};

/** Welcome-консультация: wbOffer в статусе ACTIVE (оплачен или начислен). */
export const isWelcomeConsultationActive = (
  context?: FreeMinutesEligibilityContext | null,
): boolean =>
  context?.isWbOfferActive === true;

/**
 * Порог БМ для legacy immediate call / hasTrial (M7 из env).
 * Для welcome (ACTIVE) минуты не используются — см. isWelcomeConsultationActive.
 */
export const getImmediateCallThreshold = (): number =>
  getLegacyFreeMinutesThreshold();

/**
 * Возвращает флаг, есть ли пробник.
 * Считаем, что у неавторизованного юзера он всегда есть.
 * У авторизованного - в зависимости от числа БМ и порога immediate call.
 */
export const hasTrial = (
  isUserLoggedIn: boolean,
  freeMinutesCount: number | null,
  context?: FreeMinutesEligibilityContext | null,
) => {
  if (!isUserLoggedIn) {
    return true;
  }
  if (isWelcomeConsultationActive(context)) {
    return true;
  }
  const threshold = getImmediateCallThreshold();
  if (freeMinutesCount != null && freeMinutesCount >= threshold) {
    return true;
  }
  return false;
};

// todo: maybe delete GATSBY_IS_NO_FIRST_PAYMENT_ACTION_ACTIVE action
/**
 * Return flag indicating if no firsh payment action is active for user
 * @param isUserLoggedIn
 * @param freeMinutesCount
 * @returns
 */
export const isActionActiveForUser = (
  isUserLoggedIn: boolean,
  freeMinutesCount: number | null,
  minSessionDuration: number,
) =>
  process.env.GATSBY_IS_NO_FIRST_PAYMENT_ACTION_ACTIVE === "true"
  && (isUserLoggedIn
    ? (freeMinutesCount ?? 0) >= getActualMinSessionDuration(minSessionDuration)
    : parseInt(process.env.GATSBY_FREE_MINUTES_COUNT ?? "0", 10));

/**
 * Return flag indicating if user can call to expert
 * @param expertState
 * @param sessionStatus
 * @returns
 */
export const canUserCall = (
  expertState: ExpertState | ExpertStateNew,
  sessionStatus?: SessionStatus,
) =>
  expertState === ExpertState.available && sessionStatus !== SessionStatus.in_progress;

/**
 * Return flag indicating if type of call is available for expert
 * @param rate
 * @returns
 */
export const isCallTypeAvailable = (
  rate: number | null,
) =>
  rate !== null;

/**
 * Returns actual rate for expert
 * @param rateObject
 * @returns
 */
export function getActualRate(
  rateObject?: { discounted?: number | null; base?: number | null } | null,
): null | number {
  if (isNotNullOrUndefined(rateObject?.discounted)) {
    return rateObject.discounted;
  }

  if (isNotNullOrUndefined(rateObject?.base)) {
    return rateObject.base;
  }

  return null;
}

/**
 * Returns amount of free minutes available for authorized or non-authorized user
 * @param isUserLoggedIn
 * @param freeMinutesCount
 * @returns
 */
export const getFreeMinutesForUser = (isUserLoggedIn: boolean, freeMinutesCount: number | null) =>
  (isUserLoggedIn ? freeMinutesCount ?? 0 : parseInt(process.env.GATSBY_FREE_MINUTES_COUNT ?? "0", 10));

/** Пускать в welcome-консультацию без оплаты: только статус ACTIVE оффера. */
export const isUserEligibleForWelcomeConsultation = (
  context?: FreeMinutesEligibilityContext | null,
): boolean =>
  isWelcomeConsultationActive(context);

/**
 * Retry Promise if u need it
 * @param requestFn
 * @param options
 */
export function retryPromise<T>(requestFn: () => Promise<T>, options?: {
    retries?: number | "INFINITELY";
    interval?: number;
  }): Promise<T> {
  const {
    retries = 10,
    interval = 1000,
  } = options || {};

  return new Promise((resolve, reject) => {
    let attempts = 0;

    function makeRequest() {
      attempts += 1;

      requestFn()
        .then(resolve)
        .catch((error) => {
          if (retries === "INFINITELY" || attempts < retries) {
            setTimeout(makeRequest, interval);
          } else {
            reject(error);
          }
        });
    }

    makeRequest();
  });
}

/**
 * Makes console.log only for non-prod environment
 * @param args
 */
/**
 * Включена ли отладочная печать в консоль. Включена в трёх случаях:
 *
 * 1. локальный запуск (`gatsby develop`);
 * 2. фича-стенд — адрес вида `<ветка>.feature.lunaro.ru`, его задаёт воркфлоу
 *    «Choose backend API». Нужно, чтобы QA снимал логи без пересборки с флагами;
 * 3. `GATSBY_DEBUG_LOGS=true` — ручной включатель для любой другой сборки.
 *
 * На develop, stage и проде печати нет: там адрес другой и флага нет.
 *
 * Условие продублировано в `gatsby-config.js` — там оно решает, оставить ли вызовы
 * `console` в бандле. Обе проверки обязаны совпадать: если плагин вырежет вызовы
 * на сборке, никакая проверка во время работы уже не поможет
 */
const FEATURE_STAND_HOST = "feature.lunaro.ru";

export const isDebugLogsEnabled = (): boolean =>
  process.env.NODE_ENV !== "production"
  || process.env.GATSBY_DEBUG_LOGS === "true"
  || (process.env.GATSBY_WEBSITE_URL ?? "").includes(FEATURE_STAND_HOST);

export function logToConsole(...args: any[]) {
  if (isDebugLogsEnabled()) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
}

/**
 * Captures exception to logging system with specified occurence tag
 * @param exception - The error object
 * @param occurrence - Context/tag describing where the error occurred
 */
export function captureException(exception: Error, occurrence: string) {
  // eslint-disable-next-line no-console
  console.log(`[${occurrence}]`, exception);
}

export const getCookieValue = (name: string) =>
  document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`)?.pop() || "";

export const sortExpertEdges = (
  edges: EdgesType,
  sortByTrialAllowance: boolean = false,
): EdgesType => {
  const sortedExperts = [...edges];

  sortedExperts.sort((a, b) => {
    if (!a || !b) {
      return 0;
    }

    if (a.node.stateInitial !== b.node.stateInitial) {
      if (a.node.stateInitial === "offline") {
        return 1;
      }
      if (b.node.stateInitial === "offline") {
        return -1;
      }
      if (a.node.stateInitial === "busy" && b.node.stateInitial === "available") {
        return 1;
      }
      if (b.node.stateInitial === "busy" && a.node.stateInitial === "available") {
        return -1;
      }
    }

    if (sortByTrialAllowance) {
      const aHasFreeConsultation = a.node.trialAllowance?.chat || a.node.trialAllowance?.video;

      const bHasFreeConsultation = b.node.trialAllowance?.chat || b.node.trialAllowance?.video;

      if (aHasFreeConsultation !== bHasFreeConsultation) {
        return bHasFreeConsultation ? 1 : -1;
      }
    }

    return 0;
  });

  return sortedExperts;
};

export const getSessionIdFromUrl = (): number | null => {
  const urlParams = new URLSearchParams(isBrowser() ? window.location.search : {});
  const sessionIdString = urlParams.get("sessionId") || "";
  return Number.isNaN(Number(sessionIdString)) ? null : Number(sessionIdString);
};

export const isSessionIdValid = (sessionId: number | string | null | undefined) =>
  Number(sessionId) > 0;
